import React from 'react';
import clsx from 'clsx';
import {Theme} from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import {makeStyles} from '@material-ui/styles';
import {GuestData} from '../helperFunctions';
import {Resource} from '../../../graphql/resources.datatypes';
import NumberSpinner from './NumberSpinner';

const useStyles = makeStyles((theme: Theme) => ({
    body: {
        fontSize: 16,
    },
    listItem: {
        display: 'block',
    },
    guestContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    guestContainerItem: {
        flexGrow: 2,
    },
    guestContainerItemMultiple: {
        flexGrow: 1,
        maxWidth: 100,
    },
    checkbox: {
        marginLeft: -9,
        color: theme.palette.secondary.main,
    },
}));

export interface GuestOptionListData {
    resource: Resource;
    allSelected: boolean;
    guestsData: GuestData[];
    isAvailable: boolean;
    optionId: number;
    remaining: number;
}

interface GuestOptionListControls {
    showAllCheckbox?: boolean;
    numberGuests?: boolean;
    handleGuestOptionChange: (targetData: GuestData, optionId: number, value: number, diff: number) => void;
    handleSelectAll: (optionId: number, value: number) => void;
}

type Props = GuestOptionListData & GuestOptionListControls;

const GuestOptionList = (props: Props) => {
    const {
        allSelected,
        guestsData,
        handleGuestOptionChange,
        handleSelectAll,
        isAvailable,
        optionId,
        remaining,
        resource,
        showAllCheckbox = true,
        numberGuests = true,
    } = props;
    const classes = useStyles();

    return (
        <React.Fragment>
            {showAllCheckbox && !resource!.allow_multiple && (
                <Typography
                    component="label"
                    variant="body1"
                    className={clsx(classes.body, classes.listItem)}
                >
                    <Checkbox
                        className={classes.checkbox}
                        onChange={() => handleSelectAll(optionId, allSelected ? 0 : 1)}
                        checked={allSelected}
                        disabled={!isAvailable || guestsData.length > remaining}
                    /> All Room Guests
                </Typography>
            )}
            {guestsData.map((guestData, guestIndex) => {
                const optionValue = guestData.optionValues.find(optionValue1 => optionValue1.optionId === optionId);
                const isSelected = optionValue && optionValue.value > 0;
                const allowMultiple = optionValue && optionValue.groupResourceOption.option.resource!.allow_multiple;

                return optionValue ? (
                    <div key={guestIndex} className={classes.guestContainer}>
                        <div className={classes.guestContainerItem}>
                            <Typography
                                component="label"
                                variant="body1"
                                className={clsx(classes.body, classes.listItem)}
                            >
                                <Checkbox
                                    className={classes.checkbox}
                                    name={`${optionId}_${guestData.id}`}
                                    checked={guestData.optionValues.find(optionValue2 => optionValue2.optionId === optionId)!.value > 0}
                                    onChange={(event) => handleGuestOptionChange(guestData, optionId, event.target.checked ? 1 : 0, event.target.checked ? 1 : -1)}
                                    disabled={!(isAvailable || isSelected)}
                                />
                                {numberGuests ? `${guestIndex + 1}. ` : ''}{guestData.first_name} {guestData.last_name}
                            </Typography>
                        </div>
                        {allowMultiple && (
                            <div className={clsx(classes.guestContainerItem, classes.guestContainerItemMultiple)}>
                                <NumberSpinner
                                    hideZero
                                    value={optionValue.value}
                                    min={0}
                                    max={remaining + optionValue.value}
                                    onChange={(value) => handleGuestOptionChange(guestData, optionValue.optionId, value, value - optionValue.value)}
                                    disabled={!(isAvailable || isSelected)}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    <div key={guestIndex} className={classes.guestContainer}>
                        <div className={classes.guestContainerItem}>
                            <Typography
                                component="label"
                                variant="body1"
                                className={clsx(classes.body, classes.listItem)}
                            >
                                <Checkbox
                                    className={classes.checkbox}
                                    checked={false}
                                    disabled={true}
                                />
                                {numberGuests ? `${guestIndex + 1}. ` : ''}{guestData.first_name} {guestData.last_name}
                            </Typography>
                        </div>
                    </div>
                );
            })}
        </React.Fragment>
    );
};

export default GuestOptionList;
